import React, { useState, useRef } from 'react';
import { uploadVideoForInspection } from '../services/api';

function VideoUpload({ mode, onProcessingStart, onVideoComplete, isProcessing }) {
  const [dragActive, setDragActive] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;

    const validTypes = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo'];
    if (!validTypes.includes(file.type)) {
      setError('Please upload an MP4, WEBM, MOV, or AVI video.');
      return;
    }

    if (file.size > 50 * 1024 * 1024) {
      setError('Video must be under 50MB.');
      return;
    }

    setError(null);
    setPreview(file.name);
    setUploadProgress(0);
    onProcessingStart();

    try {
      const result = await uploadVideoForInspection(file, mode, 1, (e) => {
        if (e.total) setUploadProgress(Math.round((e.loaded / e.total) * 100));
      });
      onVideoComplete(result);
    } catch (err) {
      setError(err.response?.data?.error || 'Video analysis failed. Please try again.');
      onVideoComplete(null);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    handleFile(e.dataTransfer.files[0]);
  };

  return (
    <div className="upload-container">
      <h2 className="section-title">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
          <path d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm4.5 4.5l5 2.5-5 2.5v-5z"/>
        </svg>
        Upload Video for Analysis
      </h2>
      <p className="section-desc">
        {mode === 'industry'
          ? 'Upload a factory or warehouse surveillance video for frame-by-frame safety analysis.'
          : 'Upload CCTV or site footage for regulatory compliance monitoring.'}
      </p>

      <div
        className={`dropzone ${dragActive ? 'drag-active' : ''} ${isProcessing ? 'processing' : ''}`}
        onDrop={handleDrop}
        onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
        onDragLeave={() => setDragActive(false)}
        onClick={() => !isProcessing && fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="video/mp4,video/webm,video/quicktime,video/x-msvideo"
          onChange={(e) => handleFile(e.target.files[0])}
          style={{ display: 'none' }}
        />

        {isProcessing ? (
          <div className="processing-indicator">
            <div className="spinner"></div>
            <p>Analyzing video...</p>
            <span className="processing-sub">
              {uploadProgress < 100
                ? `Uploading: ${uploadProgress}%`
                : 'Extracting frames & running AI inference on each frame'}
            </span>
            {uploadProgress < 100 && (
              <div className="progress-bar-container">
                <div className="progress-bar" style={{ width: `${uploadProgress}%` }}></div>
              </div>
            )}
          </div>
        ) : preview ? (
          <div className="preview-thumb">
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none" stroke="#2563eb" strokeWidth="2">
              <rect x="6" y="10" width="36" height="28" rx="4"/>
              <path d="M20 18l10 6-10 6V18z" fill="#2563eb"/>
            </svg>
            <p style={{ fontWeight: 500 }}>{preview}</p>
            <span className="drop-sub">Click or drop to analyze another video</span>
          </div>
        ) : (
          <div className="dropzone-content">
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="6" y="10" width="36" height="28" rx="4"/>
              <path d="M20 18l10 6-10 6V18z"/>
            </svg>
            <p className="drop-text">Drag & drop a video here</p>
            <span className="drop-sub">MP4, WEBM, MOV, AVI up to 50MB</span>
          </div>
        )}
      </div>

      {error && (
        <div className="upload-error">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M8 1a7 7 0 100 14A7 7 0 008 1zm0 10.5a.75.75 0 110-1.5.75.75 0 010 1.5zM8.75 4.5v4a.75.75 0 01-1.5 0v-4a.75.75 0 011.5 0z"/>
          </svg>
          {error}
        </div>
      )}
    </div>
  );
}

export default VideoUpload;
